import { LightModeOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../_api/auth";
import { schoolYearController } from "../../_api/controllers/scolarite.controller.ts";
import { useQuery } from "@tanstack/react-query";
import {
  useLogout,
  usePermissions,
  useRole,
  useRolePermissions,
} from "../../_api/useAuth.js";
import LoaderDisconnect from "../loader/MyLoader.tsx";
import HasRole from "./HasRole.tsx";
import MyLoader from "../loader/MyLoader.tsx";
type HeaderProps = {
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDarkMode: () => void;
  currentYear: string;
  isYearDropdownOpen: boolean;
  toggleYearDropdown: () => void;
  setCurrentYear: (year: string) => void;
  setIsYearDropdownOpen: (isOpen: boolean) => void;
};
//skeleton header
function HeaderSkeleton({ isDarkMode }: { isDarkMode: boolean }) {
  const bg = isDarkMode ? "bg-[#21262D]" : "bg-gray-200";

  return (
    <header
      className={`fixed top-0 left-20 md:left-60 right-0 z-50 h-16 border-b ${
        isDarkMode
          ? "border-[#30363D] bg-[#0D1117]/95"
          : "border-gray-200 bg-white/95"
      } backdrop-blur-md`}
    >
      <div className="flex h-full items-center justify-between px-4 sm:px-5 lg:px-6">
        {/* Year */}
        <div className={`h-9 w-36 rounded-lg ${bg} animate-pulse`} />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-lg ${bg} animate-pulse`} />

          <div
            className={`hidden h-7 w-px sm:block ${
              isDarkMode ? "bg-[#30363D]" : "bg-gray-200"
            }`}
          />

          <div className="flex items-center gap-2">
            <div
              className={`hidden h-8 w-24 rounded-lg sm:block ${bg} animate-pulse`}
            />
            <div className={`h-9 w-9 rounded-lg ${bg} animate-pulse`} />
          </div>
        </div>
      </div>
    </header>
  );
}

const Header = ({
  isDarkMode,
  toggleDarkMode,
  currentYear,
  isYearDropdownOpen,
  setCurrentYear,
  setIsYearDropdownOpen,
  toggleYearDropdown,
}: HeaderProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };
  //Handle mouse click outside for dropdowns
  const MenuDropdownRef = React.useRef<HTMLDivElement>(null);
  const logout = useLogout();

  //fetch anne scolaire
  const { data: schoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });
  const { data: activeSchoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getActive,
  });

  const schoolYearArr = schoolYear?.data || [];

  const selectedYear = schoolYearArr.find((year) => year.selected);

  setCurrentYear(selectedYear?.designation || currentYear);
  // Fermeture des menus lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //export button
      if (
        MenuDropdownRef.current &&
        !MenuDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setCurrentYear(activeSchoolYear?.data[0]?.designation || currentYear);
  }, []);

  // connected user
  const { user } = useAuth();

  //get role and permission loading state
  const { isPending: isRoleLoading } = useRole();
  const { isPending: isRolePermissionLoading } = useRolePermissions();
  const { isPending: isPermissionLoading } = usePermissions();

  return (
    <>
      {isRoleLoading || isRolePermissionLoading || isPermissionLoading ? (
        <HeaderSkeleton isDarkMode={isDarkMode} />
      ) : (
        <header
          className={`fixed top-0 left-20 md:left-60 right-0 z-50 h-16 border-b transition-colors ${
            isDarkMode
              ? "border-[#30363D] bg-[#0D1117]/95"
              : "border-gray-200 bg-white/95"
          } backdrop-blur-md`}
        >
          <MyLoader
            isLoading={logout.isPending}
            message="Déconnexion en cours..."
          />

          <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-5 lg:px-6">
            {/* ==================================================
        LEFT — SCHOOL YEAR
    ================================================== */}
            <div className="relative">
              <button
                onClick={toggleYearDropdown}
                className={`group flex h-9 items-center gap-2.5 rounded-lg border px-3 transition ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-calendar-alt text-[8px]" />
                </div>

                <div className="hidden text-left sm:block">
                  {/* <p className="text-[7px] font-medium uppercase leading-none tracking-wider text-gray-500">
                    Année scolaire
                  </p> */}

                  <p
                    className={`mt-1 whitespace-nowrap text-[10px] font-semibold leading-none ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    {currentYear}
                  </p>
                </div>

                <span className="text-[10px] font-medium sm:hidden">
                  {currentYear}
                </span>

                <i
                  className={`fas fa-chevron-down ml-1 text-[7px] text-gray-500 transition-transform duration-200 ${
                    isYearDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {/* ==================================================
        RIGHT ACTIONS
    ================================================== */}
            <div className="flex items-center gap-2">
              {/* Theme */}
              <button
                onClick={toggleDarkMode}
                title={isDarkMode ? "Mode clair" : "Mode sombre"}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-amber-400 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {isDarkMode ? (
                  <LightModeOutlined sx={{ fontSize: 16 }} />
                ) : (
                  <i className="fas fa-moon text-[11px]" />
                )}
              </button>

              {/* Separator */}
              <div
                className={`mx-1 hidden h-6 w-px sm:block ${
                  isDarkMode ? "bg-[#30363D]" : "bg-gray-200"
                }`}
              />

              {/* ==================================================
          USER
      ================================================== */}
              <div className="relative" ref={MenuDropdownRef}>
                <button
                  id="userMenuButton"
                  onClick={toggleUserMenu}
                  className={`group flex h-10 items-center gap-2 rounded-xl border p-1 pr-1 transition sm:pr-2 ${
                    isUserMenuOpen
                      ? isDarkMode
                        ? "border-blue-500/40 bg-[#161B22]"
                        : "border-blue-200 bg-blue-50/50"
                      : isDarkMode
                        ? "border-transparent hover:border-[#30363D] hover:bg-[#161B22]"
                        : "border-transparent hover:border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-user text-[10px]" />
                  </div>

                  {/* Account */}
                  <div className="hidden max-w-[150px] min-w-0 text-left lg:block">
                    <p
                      className={`truncate text-[11px] font-semibold leading-tight ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Mon compte
                    </p>

                    <p className="mt-0.5 truncate text-[10px] leading-tight text-gray-500">
                      {user?.email}
                    </p>
                  </div>

                  <i
                    className={`fas fa-chevron-down hidden text-[7px] text-gray-500 transition-transform duration-200 sm:block ${
                      isUserMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* ==================================================
            USER DROPDOWN
        ================================================== */}
                {isUserMenuOpen && (
                  <div
                    className={`absolute right-0 top-full mt-2 w-[280px] overflow-hidden rounded-xl border shadow-2xl ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#161B22]"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {/* Account header */}
                    <div
                      className={`border-b p-3.5 ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                            isDarkMode
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          <i className="fas fa-user text-xs" />

                          {/* Online status */}
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full ${
                              isDarkMode ? "bg-[#161B22]" : "bg-white"
                            }`}
                          >
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          </span>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p
                              className={`text-[11px] font-semibold ${
                                isDarkMode ? "text-gray-200" : "text-gray-800"
                              }`}
                            >
                              Mon compte
                            </p>

                            <span
                              className={`rounded-full px-1.5 py-0.5 text-[7px] font-medium ${
                                isDarkMode
                                  ? "bg-emerald-500/10 text-emerald-400"
                                  : "bg-emerald-50 text-emerald-600"
                              }`}
                            >
                              Actif
                            </span>
                          </div>

                          <p
                            className="mt-1 truncate text-[9px] text-gray-500"
                            title={user?.email}
                          >
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu */}
                    <div className="p-1.5">
                      {/* Profile */}
                      <Link
                        to="/profil"
                        className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition ${
                          isDarkMode
                            ? "text-gray-300 hover:bg-[#21262D]"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            isDarkMode
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          <i className="fas fa-user-circle text-[10px]" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-medium">
                            Voir le profil
                          </p>

                          <p className="mt-0.5 text-[8px] text-gray-500">
                            Informations personnelles
                          </p>
                        </div>

                        <i className="fas fa-chevron-right text-[7px] text-gray-500 opacity-0 transition group-hover:opacity-100" />
                      </Link>

                      {/* Settings */}
                      <HasRole allowedRoles={["Administrateur", "Comptable"]}>
                        <Link
                          to="/parametre"
                          className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition ${
                            isDarkMode
                              ? "text-gray-300 hover:bg-[#21262D]"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                              isDarkMode
                                ? "bg-purple-500/10 text-purple-400"
                                : "bg-purple-50 text-purple-600"
                            }`}
                          >
                            <i className="fas fa-cog text-[10px]" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-medium">
                              Paramètres
                            </p>

                            <p className="mt-0.5 text-[8px] text-gray-500">
                              Configuration de l'application
                            </p>
                          </div>

                          <i className="fas fa-chevron-right text-[7px] text-gray-500 opacity-0 transition group-hover:opacity-100" />
                        </Link>
                      </HasRole>
                    </div>

                    {/* Logout */}
                    <div
                      className={`border-t p-1.5 ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      }`}
                    >
                      <button
                        onClick={() => logout.mutate()}
                        className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                          isDarkMode
                            ? "text-red-400 hover:bg-red-500/10"
                            : "text-red-600 hover:bg-red-50"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            isDarkMode ? "bg-red-500/10" : "bg-red-50"
                          }`}
                        >
                          <i className="fas fa-sign-out-alt text-[10px]" />
                        </div>

                        <div className="flex-1">
                          <p className="text-[10px] font-medium">Déconnexion</p>

                          <p
                            className={`mt-0.5 text-[8px] ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Fermer votre session
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      )}
    </>
  );
};

export default Header;
